"use client"

import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, getWeek } from "date-fns"
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react"
import { enAU } from "date-fns/locale"
import { useState, type FC } from "react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { Calendar } from "@/components/ui/calendar"

type WeekPickerProps = {
  date: Date
  setDate: (date: Date) => void
  className?: string
}

const WeekPicker: FC<WeekPickerProps> = ({ date, setDate, className }: WeekPickerProps) => {
  const [open, setOpen] = useState(false)

  // Display current week information
  const currentWeekStart = startOfWeek(date, { weekStartsOn: 1 })
  const currentWeekEnd = endOfWeek(date, { weekStartsOn: 1 })
  const currentWeekNumber = getWeek(date, {
    weekStartsOn: 1,
    firstWeekContainsDate: 4,
  })
  const currentWeekLabel = `Week ${currentWeekNumber}: ${format(currentWeekStart, "dd-MMMM", { locale: enAU })} - ${format(currentWeekEnd, "dd-MMMM-yyyy", { locale: enAU })}`

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn("w-full justify-start text-left font-normal", className)}
          onClick={() => setOpen(true)}
        >
          <CalendarIcon className="mr-2 size-4" />
          {currentWeekLabel}
          <span className="text-muted-foreground ml-auto text-xs">Click to select</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-medium">Select Week</h3>
          </div>

          <div className="flex flex-col items-center justify-center">
            <Calendar
              mode="single"
              selected={date}
              onSelect={(selectedDate) => {
                if (selectedDate) {
                  // When selecting a date, get the week containing that date
                  const weekStart = startOfWeek(selectedDate, {
                    weekStartsOn: 1,
                  })
                  setDate(weekStart)
                  setOpen(false)
                }
              }}
              initialFocus
              locale={enAU}
            />
          </div>

          <div className="mt-3 flex justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDate(subWeeks(date, 1))
                setOpen(false)
              }}
            >
              <ChevronLeft className="mr-1 size-4" />
              Previous Week
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDate(new Date())
                setOpen(false)
              }}
            >
              This Week
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDate(addWeeks(date, 1))
                setOpen(false)
              }}
            >
              Next Week
              <ChevronRight className="ml-1 size-4" />
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default WeekPicker
